// FirstLoginPage.tsx · v12.10.11.1 (#1462 old first-login page: Set up your login, boxes full width with Show, ✓ in Confirm, rules one per line) · 2026-10-09
// FirstLoginPage.tsx · v12.10.11 (#1456 a new-style owner link "<slug>.<token>" goes to the practice's own /invite page) · 2026-10-08 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v10.0.18.6 · 2026-06-01 · VoiceSpectrum AI
//
// FirstLoginPage — invite-token redemption wizard:
//   1) /auth/invites/consume (with tenantSlug) → set-password step
//   2) /auth/invites/set-password → navigate to /login (MFA disabled in v18.4)
//
// v10.0.18.4 Bug D-2+E: extract slug from ?slug= query param or subdomain;
//   pass tenantSlug to /auth/invites/consume.
// v10.0.18.4 Bug E: skip MFA enrollment branch when requiresMfaSetup === false;
//   navigate directly to /login on set-password success.
// v10.0.18.6 Bug I: Fixed all interface fields from snake_case to camelCase to
//   match the API's global JsonNamingPolicy.CamelCase (Program.cs:297).
//   Also fixed set-password request body to send sessionToken (not session_token).

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Input } from '../components/ds';

const SUPPORT_EMAIL = 'support@voicespectrum.ai';

// v10.0.18.6 Bug I: All fields are camelCase to match API JsonNamingPolicy.CamelCase
interface ConsumeResponse {
  email: string;
  tenantSlug: string;       // was tenant_slug
  orgId: string;            // was org_id
  requiresPasswordSet: boolean;   // was requires_password_set
  requiresMfaSetup: boolean;      // was requires_mfa_setup
  sessionToken: string;     // was session_token
}

// v10.0.18.6 Bug I: All fields camelCase
interface EnrollResponse {
  otpauthUri: string;       // was otpauth_uri
  base32Secret: string;     // was base32_secret
  qrDataUrl: string;        // was qr_data_url
  preToken: string;         // was pre_token
}

// v10.0.18.6 Bug I: All fields camelCase
interface FinalLoginResponse { accessToken: string; }  // was access_token

type Step = 'loading' | 'set-password' | 'mfa-setup' | 'error' | 'already-used';

function PasswordStrengthBar({ password }: { password: string }) {
  const checks = [
    password.length >= 12,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
  const colors = ['', '#dc2626', '#d97706', '#ca8a04', '#16a34a', '#0891b2'];

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <div key={n} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: n <= score ? colors[score] : 'var(--line)',
            transition: 'background var(--t-fast)',
          }} />
        ))}
      </div>
      <span style={{ fontSize: 11.5, color: colors[score] ?? 'var(--muted)' }}>
        {labels[score] ?? ''}
      </span>
    </div>
  );
}

/** v12.10.11.1 (#1462, UI-PROTO-T56): a password box with Show / Hide; `match` puts a green ✓ just before Show. */
function PwBox({ id, label, value, onChange, match, autoFocus }: { id: string; label: string; value: string; onChange: (v: string) => void; match?: boolean; autoFocus?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div className="field">
      <label htmlFor={id} className="field-label field-required">{label}</label>
      <div style={{ position: 'relative' }}>
        <input id={id} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} autoComplete="new-password"
          required autoFocus={autoFocus} style={{ width: '100%', boxSizing: 'border-box', paddingRight: match ? 92 : 70 }} />
        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {match && <span role="img" aria-label="The passwords match" style={{ color: '#16a34a', fontWeight: 900 }}>✓</span>}
          <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
            style={{ border: 0, background: 'transparent', color: 'var(--muted)', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '4px 6px' }}>{show ? 'Hide' : 'Show'}</button>
        </span>
      </div>
    </div>
  );
}

/** v10.0.18.4: derive tenant slug from ?slug= query param or *.voicespectrum.ai subdomain. */
function resolveTenantSlug(searchParams: URLSearchParams): string | null {
  const fromQuery = searchParams.get('slug');
  if (fromQuery) return fromQuery;

  const host = window.location.host.toLowerCase();
  if (host.endsWith('.voicespectrum.ai')) {
    const parts = host.split('.');
    if (parts.length >= 3 && parts[0] !== 'app') {
      return parts[0];
    }
  }
  return null;
}

export default function FirstLoginPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  // v10.0.18.4: resolve tenantSlug from query param or subdomain
  const tenantSlug = resolveTenantSlug(searchParams);

  const [step, setStep] = useState<Step>('loading');
  const [consumeData, setConsumeData] = useState<ConsumeResponse | null>(null);
  const [sessionToken, setSessionToken] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [submittingPw, setSubmittingPw] = useState(false);

  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [submittingMfa, setSubmittingMfa] = useState(false);

  useEffect(() => {
    if (!token) { setStep('error'); setErrorMsg('No invite token in URL.'); return; }

    // v12.10.11 (#1456): owner links made since 12.10.11 carry "<slug>.<random>" and belong on the practice's own address,
    // in the same set-password page as every other invite. Old links (no slug prefix) keep using this page.
    const dot = token.indexOf('.');
    if (dot > 0) {
      const slug = token.slice(0, dot);
      const host = window.location.hostname;
      const base = host.startsWith('app.') ? host.slice(4) : host.split('.').slice(-2).join('.');
      window.location.replace(`https://${slug}.${base}/invite?token=${encodeURIComponent(token)}&tenant=${encodeURIComponent(slug)}`);
      return;
    }

    // v10.0.18.4 Bug D-2+E: pass tenantSlug to consume endpoint
    api.post<ConsumeResponse>('/auth/invites/consume', { token, tenantSlug })
      .then((data) => {
        setConsumeData(data);
        // v10.0.18.6 Bug I: was data.session_token (snake_case) — now data.sessionToken
        setSessionToken(data.sessionToken);

        // Persist tenantSlug so LoginPage can read it after redirect (Bug F-2)
        // v10.0.18.6 Bug I: was data.tenant_slug — now data.tenantSlug
        const resolvedSlug = data.tenantSlug ?? tenantSlug;
        if (resolvedSlug) {
          sessionStorage.setItem('vs.tenantSlug', resolvedSlug);
        }

        // v10.0.18.6 Bug I: was data.requires_password_set — now data.requiresPasswordSet
        if (data.requiresPasswordSet) setStep('set-password');
        // v10.0.18.4 Bug E: skip MFA when requiresMfaSetup is false
        // v10.0.18.6 Bug I: was data.requires_mfa_setup — now data.requiresMfaSetup
        else if (data.requiresMfaSetup) startMfaEnroll(data.sessionToken);
        else navigate('/login', { replace: true });
      })
      .catch((err: ApiError) => {
        if (err.status === 410) setStep('already-used');
        else { setStep('error'); setErrorMsg(err.message ?? 'Failed to consume invite.'); }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function startMfaEnroll(sToken: string) {
    try {
      const enroll = await api.post<EnrollResponse>('/auth/mfa/enroll', {}, {
        headers: { Authorization: `Bearer ${sToken}` },
      });
      setEnrollData(enroll);
      setStep('mfa-setup');
    } catch {
      setStep('error');
      setErrorMsg('Failed to initiate MFA enrollment.');
    }
  }

  async function handleSetPassword(e: FormEvent) {
    e.preventDefault();
    setPwError('');
    if (password.length < 12) { setPwError('Password must be at least 12 characters.'); return; }
    if (!/[A-Z]/.test(password)) { setPwError('Include at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(password)) { setPwError('Include at least one lowercase letter.'); return; }
    if (!/\d/.test(password))    { setPwError('Include at least one digit.'); return; }
    if (password !== confirm)    { setPwError('Passwords do not match.'); return; }

    setSubmittingPw(true);
    try {
      // v10.0.18.6 Bug I: was { password, session_token: sessionToken }
      // Fixed to send sessionToken (camelCase) to match API's PropertyNameCaseInsensitive=false binding
      await api.post('/auth/invites/set-password', { password, sessionToken });

      // v10.0.18.4 Bug E: when requiresMfaSetup is false, skip MFA and go to login
      // v10.0.18.6 Bug I: was consumeData?.requires_mfa_setup — now consumeData?.requiresMfaSetup
      if (consumeData?.requiresMfaSetup) {
        await startMfaEnroll(sessionToken);
      } else {
        navigate('/login', { replace: true });
      }
    } catch {
      setPwError('Failed to set password. Please try again.');
    } finally {
      setSubmittingPw(false);
    }
  }

  async function handleMfaVerify(e: FormEvent) {
    e.preventDefault();
    setMfaError('');
    if (totpCode.length !== 6) { setMfaError('Enter the 6-digit code from your authenticator app.'); return; }
    setSubmittingMfa(true);
    try {
      // v10.0.18.6 Bug I: was { code, pre_token: enrollData?.pre_token }
      // Fixed to preToken (camelCase)
      const result = await api.post<FinalLoginResponse>('/auth/mfa/verify-setup', {
        code: totpCode, preToken: enrollData?.preToken,
      });
      // v10.0.18.6 Bug I: was result.access_token — now result.accessToken
      login(result.accessToken);
      navigate('/dashboard', { replace: true });
    } catch {
      setMfaError('Invalid code. Please try again.');
    } finally {
      setSubmittingMfa(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  function shell(children: React.ReactNode) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-brand">
            <span className="brand-mark">VS</span>
            <span className="brand-text">VoiceSpectrum</span>
          </div>
          {children}
        </div>
      </div>
    );
  }

  if (step === 'loading') {
    return shell(
      <div style={{ textAlign: 'center', padding: 'var(--space-5) 0' }}>
        <div className="spinner" />
        <p className="muted text-sm" style={{ marginTop: 'var(--space-3)' }}>Setting up your account…</p>
      </div>,
    );
  }

  if (step === 'already-used') {
    return shell(
      <>
        <h1 className="auth-title">Link already used</h1>
        <p className="auth-subtitle">This invite link has already been used. Each link is single-use for security.</p>
        <Button variant="secondary" block onClick={() => { window.location.href = `mailto:${SUPPORT_EMAIL}`; }}>
          Contact support
        </Button>
      </>,
    );
  }

  if (step === 'error') {
    return shell(
      <>
        <h1 className="auth-title" style={{ color: 'var(--bad)' }}>Something went wrong</h1>
        <p className="auth-subtitle">{errorMsg}</p>
        <div className="row-2">
          <Button variant="secondary" onClick={() => window.location.reload()}>Retry</Button>
          <Button variant="ghost" onClick={() => window.location.href = `mailto:${SUPPORT_EMAIL}`}>
            Contact support
          </Button>
        </div>
      </>,
    );
  }

  if (step === 'set-password') {
    return shell(
      <>
        {/* v12.10.11.1 (#1462, UI-PROTO-T56 LOCKED): "Set up your login" with no line under it; Create a password and
            Confirm password full width, both shown from the start; the rules one per line under the boxes. */}
        <h1 className="auth-title">Set up your login</h1>
        {pwError && <div style={{ marginBottom: 'var(--space-3)' }}><Alert variant="error">{pwError}</Alert></div>}
        <form onSubmit={handleSetPassword} className="stack-3">
          <div>
            <PwBox id="fl-pw1" label="Create a password" value={password} onChange={setPassword} autoFocus />
            <PasswordStrengthBar password={password} />
          </div>
          <PwBox id="fl-pw2" label="Confirm password" value={confirm} onChange={setConfirm} match={confirm.length > 0 && confirm === password} />
          <ul aria-label="Password rules" style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 13, lineHeight: 1.6 }}>
            {([
              ['At least 12 characters', password.length >= 12],
              ['At least one uppercase letter', /[A-Z]/.test(password)],
              ['At least one lowercase letter', /[a-z]/.test(password)],
              ['At least one number', /\d/.test(password)],
            ] as [string, boolean][]).map(([t, ok]) => (
              <li key={t} style={{ color: ok ? '#15803d' : 'var(--muted)' }}><span aria-hidden="true">{ok ? '✓' : '○'}</span> {t}</li>
            ))}
          </ul>
          <Button type="submit" block loading={submittingPw}>
            {submittingPw ? 'Setting password…' : 'Create account'}
          </Button>
        </form>
      </>,
    );
  }

  if (step === 'mfa-setup') {
    return shell(
      <>
        <h1 className="auth-title">Set up two-factor authentication</h1>
        <p className="auth-subtitle">
          Scan the QR code with your authenticator app, then enter the 6-digit code.
        </p>

        {/* v10.0.18.6 Bug I: was enrollData?.qr_data_url — now enrollData?.qrDataUrl */}
        {enrollData?.qrDataUrl && (
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
            <img
              src={enrollData.qrDataUrl}
              alt="TOTP QR code"
              style={{ width: 180, height: 180, border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: 8 }}
            />
          </div>
        )}

        {/* v10.0.18.6 Bug I: was enrollData?.base32_secret — now enrollData?.base32Secret */}
        {enrollData?.base32Secret && (
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)',
            padding: '8px 10px', marginBottom: 'var(--space-4)',
            fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-all',
            color: 'var(--muted)', textAlign: 'center',
          }}>
            {enrollData.base32Secret}
            <div style={{ fontSize: 10.5, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Manual entry key
            </div>
          </div>
        )}

        {mfaError && <div style={{ marginBottom: 'var(--space-3)' }}><Alert variant="error">{mfaError}</Alert></div>}

        <form onSubmit={handleMfaVerify} className="stack-3">
          <Input
            label="Verification code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            autoComplete="one-time-code"
            required
            autoFocus
            style={{ letterSpacing: '0.25em', textAlign: 'center', fontSize: 18 }}
          />
          <Button type="submit" block loading={submittingMfa}>
            {submittingMfa ? 'Verifying…' : 'Activate and sign in'}
          </Button>
        </form>

        <p className="muted text-xs" style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
          Need help? <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
        </p>
      </>,
    );
  }

  return null;
}
